import { omit } from "lodash";
import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import AuthService from "src/services/authService";
import { useDispatch, useSelector } from "src/store";
import { selectProfile, updateProfile } from "src/store/account";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { Profile } from "@dashboard/devices/types";
import { Box, Theme, Typography } from "@mui/material";
import { getCountryData } from "@services/countryService";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";
import { LoadingIcon } from "@shared/Loading";
import { SectionFormGrid, SectionInputSlot } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { schema } from "./ProfileEditForm.validation";

const useFormStyles = makeStyles()((theme: Theme) => ({
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 420,
    },
    phoneRow: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "flex-start",
        width: "100%",
        minWidth: 0,
        "& > *": { flex: 1, minWidth: 0 },
    },
    sectionHeader: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        fontSize: 16,
    },
    formInfoText: {
        marginBottom: theme.spacing(1),
    },
}));

type FormValues = Profile;

type CountryDataState = {
    phoneCodes: SelectOption[];
    countryList: SelectOption[];
    mossCountries: string[];
};

type ProfileEditFormProps = {
    profile: API.Profile;
    onSuccess: () => void;
    onCancel: () => void;
};

const typeOfUsageOptions = [
    { value: "nb", label: i18next.t("pages:settingsPage.profileData.nonBusinessUse") },
    { value: "b", label: i18next.t("pages:settingsPage.profileData.businessUse") },
];

export const ProfileEditForm = ({ profile, onSuccess, onCancel }: ProfileEditFormProps) => {
    const { classes: formClasses } = useFormStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);

    const currentUserEmail = AuthService.getCurrentUser()?.email;
    const [isLoading, setLoading] = useState(false);
    const profileData = useSelector(selectProfile);
    const profileWithFallback = profile ?? omit(profileData, "password");
    const [data, setData] = useState<CountryDataState>({
        phoneCodes: [],
        countryList: [],
        mossCountries: [],
    });

    const validate = useValidationSchema(schema);

    const onSubmit = async (values: FormValues) => {
        try {
            const response = await dispatch(updateProfile(values)).unwrap();
            const { success, message } = response ?? {};

            if (success) {
                showSuccessToast(message || t("pages:editAccountPage.toast.profileUpdatedSuccess"));
                onSuccess();
            } else if (Array.isArray(message)) {
                const result = message.flatMap((msgObj: { message: string }) => msgObj.message);
                if (result.length > 0) {
                    showErrorToast(result[0]);
                }
            } else {
                showErrorToast(message || t("pages:editAccountPage.toast.profileUpdatedError"));
            }
        } catch (error: unknown) {
            const message =
                (error as { message?: string }).message || t("pages:editAccountPage.toast.profileUpdatedErrorUnknown");
            showErrorToast(message);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const countryData = await getCountryData();
                setData(countryData);
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message);
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    if (!data.phoneCodes.length || !data.countryList.length) {
        return (
            <SectionPanel title={t("pages:settingsPage.profileData.title")}>
                <Box className={formClasses.loadingContainer}>
                    <LoadingIcon size="block" />
                </Box>
            </SectionPanel>
        );
    }

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={profileWithFallback}
            render={({ handleSubmit, submitting, pristine }) => (
                <form onSubmit={handleSubmit}>
                    <SectionPanel
                        title={t("pages:settingsPage.profileData.title")}
                        actions={
                            <>
                                <SecondaryButton type="button" onClick={onCancel}>
                                    {t("common:button.cancel")}
                                </SecondaryButton>
                                <PrimaryButton type="submit" disabled={submitting || pristine} loading={submitting}>
                                    {t("common:button.save")}
                                </PrimaryButton>
                            </>
                        }
                    >
                        <SectionFormGrid>
                            <SectionInputSlot>
                                <TextField
                                    name="email"
                                    label={t("pages:editAccountPage.input.email.caption")}
                                    value={currentUserEmail}
                                    disabled
                                    fullWidth
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="name"
                                    label={t("pages:editAccountPage.input.fullName.caption") + " *"}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <Box className={formClasses.phoneRow}>
                                    <ControlledSelectField
                                        name="phoneCode"
                                        label={t("pages:editAccountPage.input.phoneCode.caption")}
                                        options={data.phoneCodes}
                                        fullWidth
                                        disabled={isLoading || submitting}
                                        searchable
                                    />
                                    <TextField
                                        name="phoneNumber"
                                        label={t("pages:editAccountPage.input.phoneNumber.caption") + " *"}
                                        fullWidth
                                        disabled={isLoading}
                                    />
                                </Box>
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField name="address" label={t("common:label.address")} fullWidth disabled={isLoading} />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="zipCode"
                                    label={t("pages:editAccountPage.input.zipCode.caption")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="city"
                                    label={t("pages:editAccountPage.input.city.caption")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <ControlledSelectField
                                    name="country"
                                    label={t("pages:editAccountPage.input.country.caption") + " *"}
                                    options={data.countryList}
                                    fullWidth
                                    disabled={isLoading || submitting}
                                    searchable
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <ControlledSelectField
                                    name="typeOfUsage"
                                    label={t("pages:editAccountPage.input.typeOfUsage.caption") + " *"}
                                    options={typeOfUsageOptions}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="companyName"
                                    label={t("pages:editAccountPage.input.company.caption")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <TextField
                                    name="fieldOfApplication"
                                    label={t("pages:editAccountPage.input.industry.caption")}
                                    placeholder={t("pages:editAccountPage.input.industry.placeholder")}
                                    fullWidth
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                            <SectionInputSlot fullWidth>
                                <Typography className={formClasses.sectionHeader}>
                                    {t("pages:editAccountPage.input.emailNotifications.caption")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" className={formClasses.formInfoText}>
                                    {t("pages:editAccountPage.useInformation")}
                                </Typography>
                                <ControlledCheckboxField
                                    name="newsletterChecked"
                                    disableRipple
                                    label={t("pages:editAccountPage.input.newsletter.caption")}
                                    disabled={isLoading}
                                />
                            </SectionInputSlot>
                        </SectionFormGrid>
                    </SectionPanel>
                </form>
            )}
        />
    );
};
